import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LemurClientStatus, LemurMode } from '@vsolv/packages/lemur/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { SecurityService } from '@wsphere/staff/web';
import { FeatureFlagsService } from '@wsphere/warranties/web';
import { BehaviorSubject, switchMap, tap } from 'rxjs';
import { LemurConfigWebService } from './lemur-config.web.service';

const ENVIRONMENT_OPTIONS: { value: LemurMode; label: string }[] = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'live', label: 'Live' },
];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-lemur-dialog',
  templateUrl: './lemur.dialog.html',
  standalone: false,
})
export class LemurDialog implements OnInit {
  private readonly lemurConfigSvc = inject(LemurConfigWebService);
  private readonly toastSvc = inject(ToastService);
  private readonly securitySvc = inject(SecurityService);
  private readonly featureFlagsSvc = inject(FeatureFlagsService);

  @Output() enabledChanged = new EventEmitter<boolean>();
  @Output() configuredChanged = new EventEmitter<boolean>();

  /** Set by the parent page when another payment provider (BFS) is active and must be disabled first. */
  @Input() blockedReason: string | null = null;

  readonly environmentOptions = ENVIRONMENT_OPTIONS;

  saving = false;
  editing = false;
  status$ = new BehaviorSubject<LemurClientStatus | null>(null);

  form = new FormGroup({
    environment: new FormControl<LemurMode>('sandbox', { nonNullable: true, validators: [Validators.required] }),
    apiKey: new FormControl('', { nonNullable: true }),
  });

  readonly canManageLemur$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('stf_ManageLemurApp', permissionKeys);
    }),
    tap(canManage => (canManage ? this.form.enable({ emitEvent: false }) : this.form.disable({ emitEvent: false })))
  );

  async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      const status = await this.lemurConfigSvc.getStatus();
      this.applyStatus(status);
    } catch {
      this.enabledChanged.emit(false);
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to load Lemur configuration',
        text: 'Refresh the page and try again. If this continues, contact support.',
      });
    }
  }

  startEditing(): void {
    this.editing = true;
    this.form.controls.apiKey.setValue('');
    this.form.markAsPristine();
  }

  cancelEditing(): void {
    const status = this.status$.value;
    this.editing = false;
    this.form.controls.apiKey.setValue('');
    if (status) {
      this.form.controls.environment.setValue(status.environment);
    }
    this.form.markAsPristine();
  }

  async enable(): Promise<void> {
    const status = this.status$.value;
    const apiKey = this.form.controls.apiKey.value.trim();
    if (!status?.configured && !apiKey) {
      this.toastSvc.show({
        type: 'error',
        title: 'API key required',
        text: 'Enter a Lemur API key and select Sandbox or Live before enabling financing.',
      });
      return;
    }

    if (this.blockedReason) {
      this.toastSvc.show({ type: 'error', title: 'Unable to enable Lemur financing', text: this.blockedReason });
      return;
    }

    await this.run('Unable to enable Lemur financing.', async () => {
      const environment = this.form.controls.environment.value;
      if (apiKey) {
        await this.lemurConfigSvc.setApiKey(apiKey, environment);
      } else {
        await this.persistEnvironment();
      }
      return this.lemurConfigSvc.setEnabled(true);
    });
  }

  async disable(): Promise<void> {
    await this.run('Unable to disable Lemur financing.', () => this.lemurConfigSvc.setEnabled(false));
  }

  async update(): Promise<void> {
    const apiKey = this.form.controls.apiKey.value.trim();
    const status = this.status$.value;
    if (!apiKey && !status?.configured) {
      this.toastSvc.show({
        type: 'error',
        title: 'API key required',
        text: 'Enter a Lemur API key before saving.',
      });
      return;
    }

    if (this.blockedReason) {
      this.toastSvc.show({ type: 'error', title: 'Unable to update Lemur financing', text: this.blockedReason });
      return;
    }

    await this.run('Unable to update Lemur financing.', async () => {
      const environment = this.form.controls.environment.value;
      if (apiKey) {
        await this.lemurConfigSvc.setApiKey(apiKey, environment);
      } else {
        await this.persistEnvironment();
      }
      // Configuring (or re-saving) valid credentials makes Lemur the active checkout financing option.
      return this.lemurConfigSvc.setEnabled(true);
    });
  }

  async remove(): Promise<void> {
    await this.run('Unable to remove the Lemur API key.', () => this.lemurConfigSvc.deleteApiKey());
  }

  private async persistEnvironment(): Promise<void> {
    await this.lemurConfigSvc.setEnvironment(this.form.controls.environment.value);
  }

  private async run(fallback: string, action: () => Promise<LemurClientStatus>): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    try {
      const status = await action();
      this.applyStatus(status);
      this.featureFlagsSvc.refresh();
      this.toastSvc.show({
        type: 'success',
        title: 'Lemur financing updated',
        text: status.usable
          ? 'Lemur financing is enabled for this tenant.'
          : status.configured
            ? 'Lemur is configured but disabled. New checkout applications will not be submitted.'
            : 'Lemur financing has been removed for this tenant.',
      });
    } catch (error: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: 'Lemur configuration error',
        text: this.readErrorMessage(error, fallback),
      });
    } finally {
      this.saving = false;
    }
  }

  private applyStatus(status: LemurClientStatus): void {
    this.status$.next(status);
    this.enabledChanged.emit(status.enabled);
    this.configuredChanged.emit(status.configured);
    this.editing = false;
    this.form.patchValue({ environment: status.environment, apiKey: '' }, { emitEvent: false });
    this.form.markAsPristine();
  }

  private readErrorMessage(error: unknown, fallback: string): string {
    const err = error as { error?: { message?: unknown }; message?: unknown };
    const message = err?.error?.message ?? err?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && message.every(item => typeof item === 'string')) {
      return message.join(' ');
    }
    return fallback;
  }
}
